Ext.define('SA.VectorMapView', {
    alias: 'widget.vector-map-view',
    extend: 'SA.view.SimpleContainer',

    cls: 'sa-vector-map-view',

    initComponent() {
        var maxExtent = this.model.getMaxExtent();

        this.items = [
            {
                xtype: 'vector-map-thematic-map',
                attributionHtml: SA.VectorMapConstants.getAttributionHtml(),
                maxExtent,
                style: SA.VectorMapConstants.getThematicMapStyle(),
                styleBaseUrls: SA.VectorMapConstants.getThematicMapStyleBaseUrls(),
                unit: this.model.getCountryModel().isUSA() ? 'imperial' : 'metric',
                zoomRange: SA.VectorMapConstants.getThematicMapZoomRange()
            }, {
                xtype: 'vector-map-overlay'
            }, {
                xtype: 'map-legend',
                map: this.model
            }, {
                xtype: 'vector-map-collapsible-inset-map',
                maxExtent,
                style: SA.VectorMapConstants.getInsetMapStyle()
            }, {
                xtype: 'vector-map-tool-buttons'
            }, {
                xtype: 'vector-map-zoom-slider'
            }, {
                xtype: 'load-mask',
                backgroundColor: '#fff'
            }
        ];

        this.callParent();

        this.insetMap = this.down('vector-map-collapsible-inset-map');
        this.legend = this.down('map-legend');
        this.loadMask = this.down('load-mask');
        this.overlay = this.down('vector-map-overlay');
        this.thematicMap = this.down('vector-map-thematic-map');
        this.toolButtons = this.down('vector-map-tool-buttons');
        this.zoomSlider = this.down('vector-map-zoom-slider');
    },

    addDocEventListeners() {
        this.mon(Ext.getDoc(), 'mousedown', this.onDocMouseDown, this);
        this.mon(Ext.getDoc(), 'mousemove', this.onDocMouseMove, this);
    },

    afterRender() {
        this.callParent();

        this.thematicMap.setActiveToolType(this.toolButtons.getActiveToolType());
    },

    fitExtent(extent) {
        this.thematicMap.fitExtent(extent);
    },

    getExtent() {
        return this.thematicMap.getExtent();
    },

    getZoom() {
        return this.thematicMap.getZoom();
    },

    initEvents() {
        this.insetMap.onMove(this.onInsetMapMove, this);

        this.thematicMap.onClick(this.onThematicMapClick, this);
        this.thematicMap.onHttpError(this.onThematicMapHttpError, this);
        this.thematicMap.onLoadingChange(this.refreshSpinnerVisibility, this);
        this.thematicMap.onMove(this.onThematicMapMove, this);

        this.toolButtons.onClick(this.onToolButtonsClick, this);

        this.zoomSlider.onUserChange(this.onZoomSliderUserChange, this);
        this.zoomSlider.onZoomInClick(() => this.thematicMap.zoomIn());
        this.zoomSlider.onZoomOutClick(() => this.thematicMap.zoomOut());
    },

    isOnLeftHandSide(event) {
        return event.pageX - this.getX() < this.getWidth() / 2;
    },

    loadLegendMapCenter() {
        this.legend.loadMapCenter();
    },

    onBusinessPointClick({event, point}) {
        var isPointSelected = this.thematicMap.isSelected(point),
            vicinity = this.thematicMap.queryFeatureAt(SA.GeoJson.getCoordinate(point), 'choropleth'),
            x = event.pageX - this.getX(),
            y = event.pageY - this.getY();

        this.businessesListWindow?.destroy();

        if (isPointSelected) {
            return;
        }

        this.thematicMap.setSelected(point);

        this.businessesListWindow = Ext.widget({
            xtype: 'vector-map-businesses-list-window',
            listeners: {destroy: this.onBusinessesListWindowDestroy, scope: this},
            map: this.model,
            point,
            renderTo: this.getEl(),
            vicinity
        });

        if (this.isOnLeftHandSide(event)) {
            this.businessesListWindow.showAnchored({anchor: 'cl', x: x + 20, y});
        }
        else {
            this.businessesListWindow.showAnchored({anchor: 'cr', x: x - 20, y});
        }
    },

    onBusinessesListWindowDestroy() {
        this.thematicMap.clearSelected({source: 'businesses'});

        this.businessesListWindow = null;
    },

    onDocMouseDown(ev) {
        var point = this.thematicMap.queryFeatureAt(new SA.VectorMapPoint(ev.getX() - this.thematicMap.getX(), ev.getY() - this.thematicMap.getY()), 'points');

        if (!ev.getTarget('.sa-vector-map-businesses-list-window') && !point) {
            this.businessesListWindow?.destroy();
        }
    },

    onDocMouseMove(ev) {
        var point = this.thematicMap.queryFeatureAt(new SA.VectorMapPoint(ev.getX() - this.thematicMap.getX(), ev.getY() - this.thematicMap.getY()), 'points');

        this.thematicMap.clearHovered({source: 'businesses'});
        this.thematicMap.setActiveToolType(this.toolButtons.getActiveToolType());

        if (point && !this.thematicMap.isUserDrawingZoomBoxOrPanning()) {
            this.thematicMap.setHovered(point);
            this.thematicMap.setActiveToolType(new SA.VectorMapToolType('clickPoint'));
        }
    },

    onInsetMapMove() {
        this.thematicMap.setCenter(this.insetMap.getCenter(), {suppressMoveEvents: true});
    },

    onMove(callback, scope) {
        this.insetMap.onMove(callback, scope);
        this.thematicMap.onMove(callback, scope);
        this.zoomSlider.onUserChange(callback, scope);
    },

    onMoveEnd(callback, scope) {
        this.insetMap.onMoveEnd(callback, scope);
        this.thematicMap.onMoveEnd(callback, scope);
        this.zoomSlider.onUserChangeEnd(callback, scope);
    },

    onResize() {
        this.callParent(arguments);

        this.thematicMap.resize();
    },

    onThematicMapClick({coordinate, event, position}) {
        var point = this.thematicMap.queryFeatureAt(position, 'points');

        if (point) {
            this.onBusinessPointClick({event, point});
        }
        else if (this.toolButtons.getActiveToolType().isClickInfo()) {
            this.showInfoMenu({coordinate, event});
        }
    },

    onThematicMapHttpError({exceptionClass}) {
        if (exceptionClass.endsWith('TooManyPointsException')) {
            this.thematicMap.setGeoJsonSourcesData(SA.VectorMapThematicMapGeoJsonSourcesData.get(this.model.getState(), {enableServerSideClustering: true}));
        }
        else if (exceptionClass.endsWith('TooManyClusterPointsException')) {
            this.thematicMap.clearGeoJsonSourceData('businesses');
            this.overlay.setTooManyBusinessPoints();
        }
    },

    onThematicMapMove() {
        this.insetMap.setExtentAndZoom(this.thematicMap.getExtent(), this.thematicMap.getZoom() + SA.VectorMapConstants.getInsetMapZoomOffset(), {suppressMoveEvents: true});

        this.zoomSlider.setZoom(this.thematicMap.getZoom());
    },

    onToolButtonsClick() {
        this.thematicMap.setActiveToolType(this.toolButtons.getActiveToolType());
    },

    onWebGLContextLost(callback, scope) {
        this.insetMap.onWebGLContextLost(callback, scope);
        this.thematicMap.onWebGLContextLost(callback, scope);
    },

    onZoomSliderUserChange() {
        this.thematicMap.setZoom(this.zoomSlider.getZoom(), {suppressMoveEvents: true});

        this.insetMap.setExtentAndZoom(this.thematicMap.getExtent(), this.thematicMap.getZoom() + SA.VectorMapConstants.getInsetMapZoomOffset(), {suppressMoveEvents: true});
    },

    refreshLegend() {
        this.legend.refresh();
    },

    refreshSpinnerVisibility() {
        this.overlay.setLoading(this.thematicMap.isLoading() || this.model.isLegendLoading());
    },

    removeDocEventListeners() {
        this.mun(Ext.getDoc(), 'mousedown', this.onDocMouseDown, this);
        this.mun(Ext.getDoc(), 'mousemove', this.onDocMouseMove, this);
    },

    setModelState(modelState) {
        var {businessSearch, extent} = modelState;

        this.overlay.setModelState(modelState);

        this.refreshSpinnerVisibility();

        if (businessSearch !== this.businessSearch) {
            this.thematicMap.clearGeoJsonSourceData('businesses');

            this.businessSearch = businessSearch;
        }

        this.thematicMap.setGeoJsonSourcesData(SA.VectorMapThematicMapGeoJsonSourcesData.get(modelState, {enableServerSideClustering: false}));
        this.thematicMap.setTileSourcesUrls(SA.VectorMapThematicMapTileSourcesUrls.get(modelState));
        this.thematicMap.setLayersProperties(SA.VectorMapThematicMapLayersProperties.get(modelState));

        this.thematicMap.fitExtent(extent);

        this.loadMask.hide();
    },

    showInfoMenu({coordinate, event}) {
        var attribute = this.model.getAttribute();

        Ext.widget({
            xtype: 'map-location-info-menu',
            alignDir: this.isOnLeftHandSide(event) ? 'l' : 'r',
            attribute,
            censusRelease: this.model.getCensusRelease(),
            constrainTo: this.getEl(),
            coordinate: coordinate.toXY(),
            record: this.model,
            xy: [event.pageX, event.pageY]
        });
    }
});